import { memoize } from 'lodash';
import { createSelector } from 'reselect';
import { State } from '.';
import { Message } from '../../models/message';

const getLatestMessageSelector = createSelector(
    (state: State) => state.allIds,
    (state: State) => state.byId,
    (allIds: number[], byId: Dictionary<Message>) => memoize((
        userId: Undefinable<string>
    ) => {
        const ids = allIds.filter(x => byId[x] && (userId === byId[x]!.belongToUserId));
        return byId[ids[ids.length - 1] || ''];
    })
);

const getMessageIdsSelector = createSelector(
    (state: State) => state.allIds,
    (state: State) => state.byId,
    (allIds: number[], byId: Dictionary<Message>) => memoize((
        rootMessageId: Undefinable<number>
    ) => allIds.filter(x => byId[x] && (rootMessageId === x || rootMessageId === byId[x]!.parentId)))
);

const getRootMessageIdsSelector = createSelector(
    (state: State) => state.allIds,
    (state: State) => state.byId,
    (allIds: number[], byId: Dictionary<Message>) => allIds
        .filter(x => byId[x] && !byId[x]!.parentId)
        .sort((a, b) => b - a)
);

const getUnreadMessageIdsSelector = createSelector(
    (state: State) => state.allIds,
    (state: State) => state.byId,
    (allIds: number[], byId: Dictionary<Message>) => {
        const userUnreadMessageDict = allIds.reduce(
            (prevValue, curr) => {
                if (!byId[curr]!.read) {
                    prevValue[byId[curr]!.belongToUserId] = byId[curr]!;
                }
                return prevValue;
            },
            {} as Dictionary<Message>
        );
        return Object
            .keys(userUnreadMessageDict)
            .map(x => userUnreadMessageDict[x]!.impsId)
            .sort((a, b) => b - a);
    }
);

export const selectors = {
    getLatestMessage: (
        state: State,
        userId: Undefinable<string>
    ): Undefinable<Message> => getLatestMessageSelector(state)(userId),
    getLoading: (
        state: State,
        loadingKey: string
    ): boolean => state.loading[loadingKey] || false,
    getMessage: (
        state: State,
        id: Undefinable<number>
    ): Undefinable<Message> => state.byId[id || ''],
    getMessageIds: (
        state: State,
        rootMessageId: Undefinable<number>
    ): number[] => getMessageIdsSelector(state)(rootMessageId),
    getRootMessageIds: (
        state: State,
    ): number[] => getRootMessageIdsSelector(state),
    getUnreadMessageIds: (
        state: State
    ): number[] => getUnreadMessageIdsSelector(state),
    getUserIds: (
        state: State
    ): string[] => state.userIds
};
